import React, {useMemo} from 'react'
import {
    Box,
    Text,
    Tooltip as ChakraUITooltip,
    IconButton,
    Button,
    Link as ChakraLink,

    /* Hooks */
    useMultiStyleConfig
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import useDetectDevice from '../../../../hooks/use-detect-device'
import { Link } from 'react-router-dom'

/* Utils */
import { generateStyles } from '../../utils/utils'
import { renderComponentsSwitch } from '../../utils/contentRenderer'

/* Icons */
import { InfoCircle } from '../../../icons'

/* Contexts */
import { useGetLinkData } from '../../../../contexts/category-url-context'

const PanelCtaBlock = ({
    BackgroundColor,
    ContainerBackgroundColor,
    PanelCtaBlockWidth,
    PreTitle,
    PanelCtaBlockPreTitleStyle,
    Title,
    PanelCtaBlockTitleStyle,
    Description,
    PanelCtaBlockDescriptionStyle,
    Disclaimer,
    PanelCtaBlockDisclaimerStyle,
    pcbContainerCustomStyling,
    ResponsiveBackgroundMedia,
    Tooltip,
    PanelCtaBlockTooltipStyle,
    Gradient,
    PanelCtaBlockMarginPerDevice,
    pcbPosition = 'absolute',
    pcbVariant = 'default',
    PanelCtaBlockCtas,
    ...props
}) => {
    const global = useMultiStyleConfig('GlobalCMS')
    const { isIOS, isMacOS, isMacOSChrome, isSafari, isAndroid } = useDetectDevice()
    const styles = useMultiStyleConfig('PanelCtaBlock', {
        ...props,
        PanelCtaBlockWidth: PanelCtaBlockWidth,
        imageIsConfigured: ResponsiveBackgroundMedia,
        position: pcbPosition,
        isIOS: isIOS,
        isMacOS: isMacOS,
        isSafari: isSafari,
        isAndroid: isAndroid,
        isMacOSChrome: isMacOSChrome
    })

    const getLinkData = useGetLinkData()

    const marginStyles = {
        base: PanelCtaBlockMarginPerDevice?.MarginMobile,
        md: PanelCtaBlockMarginPerDevice?.MarginTablet,
        lg: PanelCtaBlockMarginPerDevice?.MarginDesktop
    }

    const processedCtas = useMemo(() => {
        if (!PanelCtaBlockCtas) return null

        return PanelCtaBlockCtas.map(cta => ({
            ...cta,
            linkData: getLinkData(cta?.Link?.Expanded)
        }))
    }, [PanelCtaBlockCtas])


    const pcbTextContent = () => {
        return <>
            {/* Pre title */}
            {PreTitle !== '' &&
                <Text {...generateStyles(PanelCtaBlockPreTitleStyle, { ...styles.preTitleStyle })}>
                    {PreTitle}
                </Text>
            }

            {/* Title */}
            {Title !== '' &&
                <Text {...generateStyles(PanelCtaBlockTitleStyle, { ...styles.titleStyle })}>
                    {Title}
                </Text>
            }

            {/* Description */}
            {Description && Description !== '' &&
                <Text {...generateStyles(PanelCtaBlockDescriptionStyle, { ...styles.descriptionStyle })}>
                    {Description}
                </Text>
            }

            {/* Disclaimer */}
            {Disclaimer && Disclaimer !== '' &&
                <Text {...generateStyles(PanelCtaBlockDisclaimerStyle, { ...styles.disclaimerStyle })}>
                    {Disclaimer}
                </Text>
            }

            {PanelCtaBlockCtas &&
                <Box {...styles.ctasWrapper}>
                    {PanelCtaBlockCtas?.map((pcbCta) => {
                        const linkData = getLinkData(pcbCta?.Link?.Expanded)
                        const isExternalUrl = linkData?.isExternalUrl || false
                        const hasLink = !!linkData?.generatedURL
                        const linkComponent = hasLink ? (isExternalUrl ? ChakraLink : Link) : 'span'
                        const linkProps = hasLink
                            ? {
                                ...(isExternalUrl ? { href: linkData?.generatedURL } : { to: linkData?.generatedURL }),
                                ...(!linkData.stayOnSamePage && {
                                    target: linkData?.target,
                                    rel: linkData?.rel
                                })
                            }
                            : {}

                        switch(pcbCta?.Styling) {
                            case 'Text':
                                return <Box
                                    as={linkComponent}
                                    {...styles?.textStyling}
                                    {...pcbCta?.TextColor !== '' && {
                                        color: pcbCta?.TextColor
                                    }}
                                    textDecoration={pcbCta?.Underline ? 'underline' : 'none'}
                                    {...linkProps}>
                                    {pcbCta?.CtaLinkText}
                                </Box>
                            case 'Outlined':
                                return <Button
                                    variant={'outline'}
                                    as={linkComponent}
                                    {...linkProps}
                                    {...styles?.ctaStyling}
                                    backgroundColor={'transparent'}
                                    {...(pcbCta?.BorderColor !== '' || pcbCta?.PanelCtaBlockBorderWidth !== '') && {
                                        border: `${pcbCta?.PanelCtaBlockBorderWidth || '1px'} solid`,
                                        borderColor: pcbCta?.BorderColor || 'black'
                                    }}
                                    {...pcbCta?.TextColor !== '' && {
                                        color: pcbCta?.TextColor
                                    }}
                                    sx={{...styles?.ctaStyling}}
                                    >
                                        <Text as={'span'} {...styles?.ctaText} sx={{...styles?.ctaText}}>
                                            {pcbCta?.CtaLinkText}
                                        </Text>
                                </Button>
                            case 'Button':
                                return <Button
                                    as={linkComponent}
                                    {...linkProps}
                                    {...styles?.ctaStyling}
                                    {...pcbCta?.CTABackgroundColor !== '' && {
                                        backgroundColor: pcbCta?.CTABackgroundColor
                                    }}
                                    {...(pcbCta?.BorderColor !== '' || pcbCta?.PanelCtaBlockBorderWidth !== '') && {
                                        border: `${pcbCta?.PanelCtaBlockBorderWidth || '1px'} solid`,
                                        borderColor: pcbCta?.BorderColor || 'black'
                                    }}
                                    {...pcbCta?.TextColor !== '' && {
                                        color: pcbCta?.TextColor
                                    }}
                                    sx={{...styles?.ctaStyling}}
                                    >
                                        <Text as={'span'} {...styles?.ctaText} sx={{...styles?.ctaText}}>
                                            {pcbCta?.CtaLinkText}
                                        </Text>
                                </Button>
                            default:
                                break
                        }
                    })}
                </Box>
            }
        </>
    }

    return (
        <>
            {/* This variant was created because of the slider where we need some extra animation for content */}
            {pcbVariant === 'TextOnly' &&
                <>
                    <Box {...styles.container} {...pcbContainerCustomStyling} data-block-type="PanelCtaBlock">
                        {pcbTextContent()}
                    </Box>
                    {Tooltip && Tooltip !== '' && (
                        <ChakraUITooltip
                            closeOnClick={false}
                            label={Tooltip}
                            placement="bottom-end"
                            {...generateStyles(PanelCtaBlockTooltipStyle, {
                                ...global.tooltip
                            })}
                        >
                            <IconButton {...global.tooltipIcon} icon={<InfoCircle />} />
                        </ChakraUITooltip>
                    )}
                </>
            }

            {pcbVariant === 'default' &&
                <Box {...styles.wrapper} {...pcbContainerCustomStyling} margin={marginStyles} data-block-type="PanelCtaBlock">
                    {Gradient &&
                        <Box {...global.overlay}></Box>
                    }
                    <Box {...styles.container} {...pcbContainerCustomStyling}>
                        {pcbTextContent()}
                    </Box>

                    {ResponsiveBackgroundMedia &&
                        renderComponentsSwitch(ResponsiveBackgroundMedia?.Expanded)
                    }

                    {Tooltip && Tooltip !== '' && (
                        <ChakraUITooltip
                            closeOnClick={false}
                            label={Tooltip}
                            placement="bottom-end"
                            {...generateStyles(PanelCtaBlockTooltipStyle, {
                                ...global.tooltip
                            })}
                        >
                            <IconButton {...global.tooltipIcon} icon={<InfoCircle />} />
                        </ChakraUITooltip>
                    )}
                </Box>
            }
        </>
    )
}

PanelCtaBlock.propTypes = {
    BackgroundColor: PropTypes.string,
    ContainerBackgroundColor: PropTypes.string,
    PanelCtaBlockWidth: PropTypes.string,
    PreTitle: PropTypes.string,
    PanelCtaBlockPreTitleStyle: PropTypes.object,
    Title: PropTypes.string,
    PanelCtaBlockTitleStyle: PropTypes.object,
    Description: PropTypes.string,
    PanelCtaBlockDescriptionStyle: PropTypes.object,
    Disclaimer: PropTypes.string,
    PanelCtaBlockDisclaimerStyle: PropTypes.object,
    pcbContainerCustomStyling: PropTypes.object,
    ResponsiveBackgroundMedia: PropTypes.object,
    Tooltip: PropTypes.string,
    PanelCtaBlockTooltipStyle: PropTypes.object,
    Gradient: PropTypes.bool,
    PanelCtaBlockMarginPerDevice: PropTypes.shape({
        MarginMobile: PropTypes.string,
        MarginTablet: PropTypes.string,
        MarginDesktop: PropTypes.string
    }),
    pcbPosition: PropTypes.string,
    pcbVariant: PropTypes.string,
    PanelCtaBlockCtas: PropTypes.array
}

export default PanelCtaBlock
